import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { PersonalizationsService } from '../../base/services/personalizations/personalizations.service';
import { Environment } from '../../models/environment';
import { SgBranding } from '../models/branding.model';
import { SgColorUtils } from '../utils/colors-utils';

interface ICSSVariableValue {
    css_variable: string;
    css_variable_value: string;
}

@Injectable({
    providedIn: 'root',
})
export class BrandingService {
    public logoSubscriber: BehaviorSubject<string>;
    public logoSubscriberSidebar: BehaviorSubject<string>;
    environment: Environment;

    constructor(private _personalization: PersonalizationsService) {
        this.logoSubscriber = new BehaviorSubject<string>('assets/images/logo-ivcert.png');
        this.logoSubscriberSidebar = new BehaviorSubject<string>('assets/images/logo-ivcert-blanco.png');
    }

    public static readonly CSS_VARIABLES = {
        ROOT: ':root',
        PRIMARY_COLOR: '--sg-primary-color',
        PRIMARY_COLOR_HOVER: '--sg-primary-color-hover',
        PRIMARY_COLOR_DISABLED: '--sg-primary-color-disabled',
        SECONDARY_COLOR: '--sg-secondary-color',
        PRIMARY_COLOR_TEXT: '--sg-primary-color-text',
        TEXT_COLOR: '--sg-text-color',
        TEXT_COLOR_HOVER: '--sg-text-color-hover',
        HEADER_COLOR: '--sg-header-color',
        FOOTER_COLOR: '--sg-footer-color',
        LOGO_HEIGHT: '--sg-logo-height',
    };
    public static CSS_VARIABLES_VALUES: Map<string, string>;
    public static readonly DOCUMENT_ELEMENTS_TYPE = {
        STYLE: 'style',
    };
    public static readonly LUMINOSITY_VALUES = {
        LIGHT: 35,
        MEDIUM: 45,
        STRONG: 120,
    };

    public static withApplicationLayoutColor(branding: SgBranding, LUMINOSITY_VALUES) {
        const darkenPrimaryColor = SgColorUtils.changeColorLuminosity(branding.applicationLayoutColor, LUMINOSITY_VALUES.MEDIUM);
        const lightenPrimaryColor = SgColorUtils.changeColorLuminosity(branding.applicationLayoutColor, LUMINOSITY_VALUES.STRONG);

        const hasCSSVariablesValues =
            BrandingService.CSS_VARIABLES_VALUES.has(BrandingService.CSS_VARIABLES.PRIMARY_COLOR) &&
            BrandingService.CSS_VARIABLES_VALUES.has(BrandingService.CSS_VARIABLES.PRIMARY_COLOR_HOVER) &&
            BrandingService.CSS_VARIABLES_VALUES.has(BrandingService.CSS_VARIABLES.PRIMARY_COLOR_DISABLED);

        if (hasCSSVariablesValues) {
            const CSSVariablesToUpdate: ICSSVariableValue[] = [
                {
                    css_variable: BrandingService.CSS_VARIABLES.PRIMARY_COLOR,
                    css_variable_value: `${branding.applicationLayoutColor}`,
                },
                {
                    css_variable: BrandingService.CSS_VARIABLES.PRIMARY_COLOR_HOVER,
                    css_variable_value: `${darkenPrimaryColor}`,
                },
                {
                    css_variable: BrandingService.CSS_VARIABLES.PRIMARY_COLOR_DISABLED,
                    css_variable_value: `${lightenPrimaryColor}`,
                },
            ];
            BrandingService.updateCSSVariablesValues(CSSVariablesToUpdate);
        }
    }

    public static withApplicationTextColor(branding: SgBranding, LUMINOSITY_VALUES) {
        const darkenButtonTextColor = SgColorUtils.changeColorLuminosity(branding.applicationTextColor, LUMINOSITY_VALUES.LIGHT);

        const hasCSSVariablesValues =
            BrandingService.CSS_VARIABLES_VALUES.has(BrandingService.CSS_VARIABLES.TEXT_COLOR) &&
            BrandingService.CSS_VARIABLES_VALUES.has(BrandingService.CSS_VARIABLES.TEXT_COLOR_HOVER);

        if (hasCSSVariablesValues) {
            const CSSVariablesToUpdate: ICSSVariableValue[] = [
                {
                    css_variable: BrandingService.CSS_VARIABLES.TEXT_COLOR,
                    css_variable_value: `${branding.applicationTextColor}`,
                },
                {
                    css_variable: BrandingService.CSS_VARIABLES.TEXT_COLOR_HOVER,
                    css_variable_value: `${darkenButtonTextColor}`,
                },
            ];
            BrandingService.updateCSSVariablesValues(CSSVariablesToUpdate);
        }
    }

    public static withBrandingHeaderColor(branding: SgBranding) {
        const hasCSSVariablesValues = BrandingService.CSS_VARIABLES_VALUES.has(BrandingService.CSS_VARIABLES.HEADER_COLOR);
        if (hasCSSVariablesValues) {
            const CSSVariablesToUpdate: ICSSVariableValue[] = [
                {
                    css_variable: BrandingService.CSS_VARIABLES.HEADER_COLOR,
                    css_variable_value: `${branding.headerColor}`,
                },
            ];
            BrandingService.updateCSSVariablesValues(CSSVariablesToUpdate);
        }
    }

    public static withBrandingFooterColor(branding: SgBranding) {
        const hasCSSVariablesValues = BrandingService.CSS_VARIABLES_VALUES.has(BrandingService.CSS_VARIABLES.FOOTER_COLOR);
        if (hasCSSVariablesValues) {
            const CSSVariablesToUpdate: ICSSVariableValue[] = [
                {
                    css_variable: BrandingService.CSS_VARIABLES.FOOTER_COLOR,
                    css_variable_value: `${branding.footerColor}`,
                },
            ];
            BrandingService.updateCSSVariablesValues(CSSVariablesToUpdate);
        }
    }

    public static withBrandingLogo(branding: SgBranding, LUMINOSITY_VALUES) {
        const darkenPrimaryColor = SgColorUtils.changeColorLuminosity(branding.applicationLayoutColor, LUMINOSITY_VALUES.MEDIUM);
        const lightenPrimaryColor = SgColorUtils.changeColorLuminosity(branding.applicationLayoutColor, LUMINOSITY_VALUES.STRONG);
        const darkenButtonTextColor = SgColorUtils.changeColorLuminosity(branding.applicationTextColor, LUMINOSITY_VALUES.LIGHT);

        const hasCSSVariablesValues =
            BrandingService.CSS_VARIABLES_VALUES.has(BrandingService.CSS_VARIABLES.PRIMARY_COLOR) &&
            BrandingService.CSS_VARIABLES_VALUES.has(BrandingService.CSS_VARIABLES.PRIMARY_COLOR_HOVER) &&
            BrandingService.CSS_VARIABLES_VALUES.has(BrandingService.CSS_VARIABLES.PRIMARY_COLOR_DISABLED) &&
            BrandingService.CSS_VARIABLES_VALUES.has(BrandingService.CSS_VARIABLES.TEXT_COLOR) &&
            BrandingService.CSS_VARIABLES_VALUES.has(BrandingService.CSS_VARIABLES.TEXT_COLOR_HOVER);

        if (hasCSSVariablesValues) {
            const CSSVariablesToUpdate: ICSSVariableValue[] = [
                {
                    css_variable: BrandingService.CSS_VARIABLES.PRIMARY_COLOR,
                    css_variable_value: `${branding.applicationLayoutColor}`,
                },
                {
                    css_variable: BrandingService.CSS_VARIABLES.PRIMARY_COLOR_HOVER,
                    css_variable_value: `${darkenPrimaryColor}`,
                },
                {
                    css_variable: BrandingService.CSS_VARIABLES.PRIMARY_COLOR_DISABLED,
                    css_variable_value: `${lightenPrimaryColor}`,
                },
                {
                    css_variable: BrandingService.CSS_VARIABLES.TEXT_COLOR,
                    css_variable_value: `${branding.applicationTextColor}`,
                },
                {
                    css_variable: BrandingService.CSS_VARIABLES.TEXT_COLOR_HOVER,
                    css_variable_value: `${darkenButtonTextColor}`,
                },
            ];
            BrandingService.updateCSSVariablesValues(CSSVariablesToUpdate);
        }
    }

    public static updateCSSVariablesValues(CSSVariablesToUpdate: ICSSVariableValue[]) {
        CSSVariablesToUpdate.forEach((CSSVariable: ICSSVariableValue) =>
            BrandingService.CSS_VARIABLES_VALUES.set(CSSVariable.css_variable, CSSVariable.css_variable_value)
        );
    }

    public static createStyleTag(documentHtml: Document, branding: SgBranding) {
        const head = documentHtml.head;
        const style = documentHtml.createElement(
            BrandingService.DOCUMENT_ELEMENTS_TYPE.STYLE
        );
        const hasBranding = branding !== null;
        const hasLogo = hasBranding && branding.currentLogo;
        const hasApplicationLayoutColor =
            hasBranding && branding.applicationLayoutColor;
        const hasApplicationTextColor =
            hasBranding && branding.applicationTextColor;
        const shouldHaveTextAndLayoutColorWithLogo =
            hasApplicationLayoutColor && hasApplicationTextColor && hasLogo;
        const hasHeaderColor = hasBranding && branding.headerColor;
        const hasFooterColor = hasBranding && branding.footerColor;

        if (hasApplicationLayoutColor) {
            BrandingService.withApplicationLayoutColor(
                branding,
                BrandingService.LUMINOSITY_VALUES
            );
        }

        if (hasApplicationTextColor) {
            BrandingService.withApplicationTextColor(
                branding,
                BrandingService.LUMINOSITY_VALUES
            );
        }

        if (shouldHaveTextAndLayoutColorWithLogo) {
            BrandingService.withBrandingLogo(
                branding,
                BrandingService.LUMINOSITY_VALUES
            );
        }

        if (hasHeaderColor) {
            BrandingService.withBrandingHeaderColor(branding);
        }

        if (hasFooterColor) {
            BrandingService.withBrandingFooterColor(branding);
        }

        const css = `${BrandingService.CSS_VARIABLES.ROOT} {
            ${BrandingService.CSS_VARIABLES.PRIMARY_COLOR}: ${BrandingService.CSS_VARIABLES_VALUES.get(
            BrandingService.CSS_VARIABLES.PRIMARY_COLOR
        )};
            ${BrandingService.CSS_VARIABLES.PRIMARY_COLOR_HOVER}: ${BrandingService.CSS_VARIABLES_VALUES.get(
            BrandingService.CSS_VARIABLES.PRIMARY_COLOR_HOVER
        )};
            ${BrandingService.CSS_VARIABLES.PRIMARY_COLOR_DISABLED}: ${BrandingService.CSS_VARIABLES_VALUES.get(
            BrandingService.CSS_VARIABLES.PRIMARY_COLOR_DISABLED
        )};
            ${BrandingService.CSS_VARIABLES.SECONDARY_COLOR}: ${BrandingService.CSS_VARIABLES_VALUES.get(
            BrandingService.CSS_VARIABLES.SECONDARY_COLOR
        )};
            ${BrandingService.CSS_VARIABLES.PRIMARY_COLOR_TEXT}: ${BrandingService.CSS_VARIABLES_VALUES.get(
            BrandingService.CSS_VARIABLES.PRIMARY_COLOR_TEXT
        )};
            ${BrandingService.CSS_VARIABLES.TEXT_COLOR}:  ${BrandingService.CSS_VARIABLES_VALUES.get(
            BrandingService.CSS_VARIABLES.TEXT_COLOR
        )};
            ${BrandingService.CSS_VARIABLES.TEXT_COLOR_HOVER}: ${BrandingService.CSS_VARIABLES_VALUES.get(
            BrandingService.CSS_VARIABLES.TEXT_COLOR_HOVER
        )};
            ${BrandingService.CSS_VARIABLES.HEADER_COLOR}: ${BrandingService.CSS_VARIABLES_VALUES.get(
            BrandingService.CSS_VARIABLES.HEADER_COLOR
        )};
            ${BrandingService.CSS_VARIABLES.FOOTER_COLOR}: ${BrandingService.CSS_VARIABLES_VALUES.get(
            BrandingService.CSS_VARIABLES.FOOTER_COLOR
        )};
            ${BrandingService.CSS_VARIABLES.LOGO_HEIGHT}: ${BrandingService.CSS_VARIABLES_VALUES.get(
            BrandingService.CSS_VARIABLES.LOGO_HEIGHT
        )};
        }`;
        style.appendChild(documentHtml.createTextNode(css));
        head.appendChild(style);
    }

    private setDefaultCSSVariables(): void {
        BrandingService.CSS_VARIABLES_VALUES = new Map<string, string>([
            [BrandingService.CSS_VARIABLES.PRIMARY_COLOR, '#8FCE3D'],
            [BrandingService.CSS_VARIABLES.PRIMARY_COLOR_HOVER, '#4E7D10'],
            [BrandingService.CSS_VARIABLES.PRIMARY_COLOR_DISABLED, '#D1E3BA'],
            [BrandingService.CSS_VARIABLES.SECONDARY_COLOR, '#00222D'],
            [BrandingService.CSS_VARIABLES.PRIMARY_COLOR_TEXT, '#0F3E4D'],
            [BrandingService.CSS_VARIABLES.TEXT_COLOR, '#FFFFFF'],
            [BrandingService.CSS_VARIABLES.TEXT_COLOR_HOVER, '#FFFFFF'],
            [BrandingService.CSS_VARIABLES.HEADER_COLOR, '#0F3E4D'],
            [BrandingService.CSS_VARIABLES.FOOTER_COLOR, '#0F3E4D'],
            [BrandingService.CSS_VARIABLES.LOGO_HEIGHT, '46px'],
        ]);
    }

    private generateByEnviroment(): SgBranding {
        this.environment = this._personalization.sendEnv;
        const branding: SgBranding = new SgBranding();
        if (this.environment.branding) {
            branding.applicationLayoutColor = this.environment.branding.application_layout_color ?? '#8fce3d';
            branding.applicationTextColor = this.environment.branding.application_text_color ?? '#ffffff';
            branding.headerColor = this.environment.branding.header_color ?? null;
            branding.footerColor = this.environment.branding.footer_color ?? null;
            branding.signatureColor = this.environment.branding.signature_color ?? null;
            branding.currentLogo = this.environment.branding.logo ?? null;
            branding.currentLogoSidebar = this.environment.branding.logoSidebar ?? null;
        }
        return branding;
    }

    public generateBranding(branding?: SgBranding): void {

        this.setDefaultCSSVariables();
        const documentHtml = window.document;

        branding = branding === undefined ? this.generateByEnviroment() : branding;

        if (documentHtml) {
            BrandingService.createStyleTag(documentHtml, branding);
        }

        if (branding && branding.currentLogo) {
            this.logoSubscriber.next(branding.currentLogo);
        }

        if (branding && branding.currentLogoSidebar) {
            this.logoSubscriberSidebar.next(branding.currentLogoSidebar);
        }
    }
}
